import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { useOnShown } from "../../lib/inApp";
import { lastLoaded, loadKept, prefetchKept } from "../../lib/kept";
import type { PartnerDetail } from "../../../shared/types";

/** Start loading a partner as a finger rests on their row, so their page opens ready. */
export function prefetchPartner(id: string): void {
  prefetchKept(`partner:${id}`, () => api.partners.get(id));
}

/**
 * One partner's owner-side detail, loaded by id. Picking another partner keeps the last one on screen
 * until the new one arrives, so the iPad's detail pane never flashes empty.
 */
export function usePartnerDetail(id: string) {
  const [detail, setDetail] = useState<PartnerDetail | null>(() => lastLoaded(`partner:${id}`));
  const [loading, setLoading] = useState(() => !lastLoaded(`partner:${id}`));
  const [notFound, setNotFound] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => loadKept(`partner:${id}`, () => api.partners.get(id)), [id]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    setNotFound(false);
    load()
      .then((d) => alive && setDetail(d))
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setErr(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [load]);

  const refetch = useCallback(async () => {
    const d = await load();
    setDetail(d);
    return d;
  }, [load]);
  // Why quiet: the page keeps what it shows; a failed read leaves it as it is.
  useOnShown(() => void refetch().catch(() => {}));

  return { detail, loading, notFound, err, refetch };
}
